/**
 * @file MediaIdField.tsx
 * @description Фото, видео или звук секции — только из медиатеки магазина
 * (phase19.16 Р2; SB97; решение 2026-10-09 «видео — только файл»):
 * миниатюра по id, «Выбрать из медиатеки» / «Заменить», «Убрать».
 * Payload хранит только id — адрес миниатюры строится `contentMediaUrl`.
 * `openOnMount` — сразу открыть медиатеку (новый элемент «Видео», фото фона
 * у заготовки «Фото на весь экран»).
 */

import { useEffect, useState } from "react";

import { Label } from "../../../ui/label";
import { Button } from "../../../ui/button";
import { MediaPickerDialog } from "../../../site-view/media/MediaPickerDialog";
import { rememberMediaSize } from "../../../../hooks/site-builder/useMediaSizes";
import { contentMediaUrl } from "../../../../lib/api-content-media";
import { cn } from "@/lib/utils";

interface Props {
  label: string;
  value: string | null;
  onChange: (mediaId: string | null) => void;
  hint?: string;
  accept?: readonly string[];
  /** Картинка (миниатюра), видео или звук (без превью). */
  kind?: "image" | "video" | "audio";
  /** Без «Убрать» (обязательный файл). */
  required?: boolean;
  openOnMount?: boolean;
  onOpened?: () => void;
  error?: string | null;
}

export function MediaIdField({ label, value, onChange, hint, accept, kind = "image", required = false, openOnMount = false, onOpened, error }: Props) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!openOnMount) return;
    setOpen(true);
    onOpened?.();
  }, [openOnMount, onOpened]);

  return (
    <div className="flex flex-col gap-2">
      <Label>{label}</Label>
      {kind === "image" ? (
        <div className={cn("flex h-24 w-full max-w-60 items-center justify-center overflow-hidden rounded-xl bg-surface-low", error ? "[box-shadow:inset_0_0_0_1px_var(--destructive)]" : "[box-shadow:inset_0_0_0_1px_var(--border)]")}>
          {value ? <img src={contentMediaUrl(value)} alt="" className="max-h-full max-w-full object-contain" /> : <span className="type-caption text-on-surface-variant">не выбрано</span>}
        </div>
      ) : (
        <span className="type-caption text-on-surface-variant">{value ? "Файл выбран" : "Файл не выбран"}</span>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setOpen(true)}>
          {value ? "Заменить" : "Выбрать из медиатеки"}
        </Button>
        {value && !required ? (
          <Button variant="ghost" onClick={() => onChange(null)}>
            Убрать
          </Button>
        ) : null}
      </div>
      {error ? <p className="type-caption text-destructive">{error}</p> : null}
      {hint ? <p className="type-caption text-on-surface-variant">{hint}</p> : null}
      <MediaPickerDialog
        open={open}
        title={label}
        hint={hint}
        accept={accept}
        mediaKind={kind}
        onClose={() => setOpen(false)}
        onSelect={(media) => {
          // Размеры файла — для рамки кадра и предупреждений (SB120 этап 1).
          rememberMediaSize(media);
          onChange(media.id);
          setOpen(false);
        }}
      />
    </div>
  );
}
